import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Track last modified tab and timestamp
  lastModifiedTabId: null,
  lastModifiedTimestamp: null,
  lastModifiedAction: null,

  // Track overall app state
  totalTabsCount: 1,
  hasUnsavedChanges: false,
  unsavedTabIds: [],

  // Global settings that apply across all tabs
  appSettings: {
    theme: 'light',
    language: 'en',
    autoSave: false,
    autoSaveInterval: 60000, // 1 minute
  },

  // Session information
  sessionInfo: {
    sessionId: null,
    startTime: null,
    lastActivityTime: null,
  },

  // Global notifications/messages
  notifications: [],

  // Loading states
  isLoading: false,
  loadingMessage: '',

  // UI Flags - migrated from localStorage
  uiFlags: {
    loadCheck: false,
    modelOpenDuplicate: false,
    edgeChange: false,
    globalExitBtn: false,
    autoSaveStreamNumber: false,
    appWiz: false,
    focusedColumn: '',
  },

  // Database version info
  db_version: null,

  // Canvas clear signal
  clearCanvasFlag: false,
};

const globalSlice = createSlice({
  name: 'global',
  initialState,
  reducers: {
    // Track when a tab is modified
    trackTabModification: (state, action) => {
      const { tabId, actionType } = action.payload;
      state.lastModifiedTabId = tabId;
      state.lastModifiedTimestamp = Date.now();
      state.lastModifiedAction = actionType;
      state.hasUnsavedChanges = true;
      if (!state.unsavedTabIds.includes(tabId)) {
        state.unsavedTabIds.push(tabId);
      }
    },

    // Mark a tab as saved
    markTabAsSaved: (state, action) => {
      const tabId = action.payload;
      state.unsavedTabIds = state.unsavedTabIds.filter(id => id !== tabId);
      if (state.unsavedTabIds.length === 0) {
        state.hasUnsavedChanges = false;
      }
    },

    // Mark all tabs as saved
    markAllTabsAsSaved: (state) => {
      state.unsavedTabIds = [];
      state.hasUnsavedChanges = false;
    },

    // Update tabs count (called when tabs are added/removed)
    updateTabsCount: (state, action) => {
      state.totalTabsCount = action.payload;
    },

    // Update app settings
    updateAppSettings: (state, action) => {
      state.appSettings = { ...state.appSettings, ...action.payload };
    },

    // Set loading state
    setLoading: (state, action) => {
      const { isLoading, message = '' } = action.payload;
      state.isLoading = isLoading;
      state.loadingMessage = message;
    },

    // Add notification
    addNotification: (state, action) => {
      const notification = {
        id: Date.now(),
        timestamp: Date.now(),
        ...action.payload
      };
      state.notifications.push(notification);
    },

    // Remove notification
    removeNotification: (state, action) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload);
    },

    // Clear all notifications
    clearNotifications: (state) => {
      state.notifications = [];
    },

    // Initialize session
    initializeSession: (state, action) => {
      state.sessionInfo = {
        sessionId: action.payload?.sessionId || `session-${Date.now()}`,
        startTime: Date.now(),
        lastActivityTime: Date.now(),
      };
    },

    // Update last activity time
    updateLastActivity: (state) => {
      state.sessionInfo.lastActivityTime = Date.now();
    },

    // UI Flags - migrated from localStorage
    setUiFlag: (state, action) => {
      const { flag, value } = action.payload;
      if (Object.prototype.hasOwnProperty.call(state.uiFlags, flag)) {
        state.uiFlags[flag] = value;
      }
    },
    setLoadCheck: (state, action) => {
      state.uiFlags.loadCheck = action.payload;
    },
    setModelOpenDuplicate: (state, action) => {
      state.uiFlags.modelOpenDuplicate = action.payload;
    },
    setEdgeChange: (state, action) => {
      state.uiFlags.edgeChange = action.payload;
    },
    setGlobalExitBtn: (state, action) => {
      state.uiFlags.globalExitBtn = action.payload;
    },
    setAutoSaveStreamNumber: (state, action) => {
      state.uiFlags.autoSaveStreamNumber = action.payload;
    },
    setAppWiz: (state, action) => {
      state.uiFlags.appWiz = action.payload;
    },
    setFocusedColumn: (state, action) => {
      state.uiFlags.focusedColumn = action.payload;
    },
    resetUiFlags: (state) => {
      state.uiFlags = initialState.uiFlags;
    },

    // Database version
    setDbVersion: (state, action) => {
      state.db_version = action.payload;
    },

    // Canvas clear signal
    setClearCanvasFlag: (state, action) => {
      state.clearCanvasFlag = action.payload;
    },

    // Reset global state
    resetGlobalState: () => {
      return initialState;
    },
  },
});

export const {
  trackTabModification,
  markTabAsSaved,
  markAllTabsAsSaved,
  updateTabsCount,
  updateAppSettings,
  setLoading,
  addNotification,
  removeNotification,
  clearNotifications,
  initializeSession,
  updateLastActivity,
  // UI Flags
  setUiFlag,
  setLoadCheck,
  setModelOpenDuplicate,
  setEdgeChange,
  setGlobalExitBtn,
  setAutoSaveStreamNumber,
  setAppWiz,
  setFocusedColumn,
  resetUiFlags,
  // Database version
  setDbVersion,
  // Canvas clear
  setClearCanvasFlag,
  resetGlobalState,
} = globalSlice.actions;

// Selectors
export const selectLastModifiedInfo = (state) => ({
  tabId: state.global.lastModifiedTabId,
  timestamp: state.global.lastModifiedTimestamp,
  action: state.global.lastModifiedAction,
});

export const selectHasUnsavedChanges = (state) => state.global.hasUnsavedChanges;
export const selectUnsavedTabIds = (state) => state.global.unsavedTabIds;
export const selectAppSettings = (state) => state.global.appSettings;
export const selectIsLoading = (state) => state.global.isLoading;
export const selectNotifications = (state) => state.global.notifications;
export const selectSessionInfo = (state) => state.global.sessionInfo;

// UI Flags selectors
export const selectUiFlags = (state) => state.global.uiFlags;
export const selectLoadCheck = (state) => state.global.uiFlags.loadCheck;
export const selectModelOpenDuplicate = (state) => state.global.uiFlags.modelOpenDuplicate;
// autoStremId is now stored per-tab in canvas.checked (selectActiveTabAutoStremId in tabSlice)
export const selectEdgeChange = (state) => state.global.uiFlags.edgeChange;
export const selectGlobalExitBtn = (state) => state.global.uiFlags.globalExitBtn;
export const selectAutoSaveStreamNumber = (state) => state.global.uiFlags.autoSaveStreamNumber;
export const selectAppWiz = (state) => state.global.uiFlags.appWiz;
export const selectFocusedColumn = (state) => state.global.uiFlags.focusedColumn;

// Database version selector
export const selectDbVersion = (state) => state.global.db_version;

// Canvas clear signal selector
export const selectClearCanvasFlag = (state) => state.global.clearCanvasFlag;

export default globalSlice.reducer;
